import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import {
    useClientUser,
    useItemQuantity,
    useTotalWeightSubscription,
    useTotalCubesSubscription,
    useOrderNotes,
} from '@/hooks';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { startCase } from 'lodash';
import { hourMinuteFormatter } from '@/constants/formats';
import { recommendedEquipmentLabels } from '@/constants/recommendedEquipmentLabels';
import { OnwardCard } from '../../OrderDetailsPage/Card';
import { CardTitle, CardItem, CardSubtitle, CardItemBold } from '../../OrderDetailsPage/blocks';
import { Context } from '../store';
import { genAttributes, useWarehouseAggregate } from '@onward-delivery/core';
import { formatInTimeZone } from 'date-fns-tz';
import { asDateInTZ } from '@/utilities/convertToISO';
import { delWindowToFormattedDateRange, delWindowToFormattedDateSingle } from '@/utilities/delWindowToFormattedDate';

// Photo gallery row — flex-wraps thumbnails left-to-right with consistent spacing.
// Replaces the prior pattern of stacking images via separate CardRows. Falls back to
// a "No uploads" line when the array is empty.
const PhotoRow = ({ urls, keyPrefix }) => {
    if (!urls || urls.length === 0) {
        return (
            <Grid container item style={{ marginTop: '0.25rem', marginBottom: '0.25rem' }}>
                <CardItem>No uploads</CardItem>
            </Grid>
        );
    }
    return (
        <Grid
            container
            css={css`
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                margin-top: 0.25rem;
                margin-bottom: 0.25rem;
            `}
        >
            {urls.map((url, idx) => (
                <a
                    key={`${keyPrefix}-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    css={css`
                        display: inline-flex;
                    `}
                >
                    <img
                        src={url}
                        css={css`
                            width: 100px;
                            height: 100px;
                            object-fit: cover;
                            border-radius: 4px;
                        `}
                    />
                </a>
            ))}
        </Grid>
    );
};

export default function ItemsAndDocs() {
    const { state, callbacks } = useContext(Context);
    const order = state?.updated;
    const { zip: pickup_zip } = genAttributes(order, true);
    const { zip: dropoff_zip } = genAttributes(order);

    const { user_id, accountType, locations } = useClientUser();

    const displayDeliveryTime = order?.carrier_id && user_id !== order?.carrier_id && !!order?.del_window_start;
    const displayNonSaasDeliveryTime =
        order?.carrier_id && user_id !== order?.carrier_id && !!order?.del_hour && !!order?.del_end;

    let timeRangeStart = '';
    let timeRangeEnd = '';
    if (displayDeliveryTime) {
        timeRangeStart = order.del_window_start;
        timeRangeEnd = order.del_window_end;
    } else if (displayNonSaasDeliveryTime) {
        const tz = zipcode_to_timezone.lookup(order.dropoff_zip) || 'America/New_York';

        timeRangeStart = new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
        timeRangeEnd = new Date(new Date().toLocaleString('en-US', { timeZone: tz }));

        timeRangeStart.setHours(order.del_hour);
        timeRangeEnd.setHours(order.del_end);

        if (order.del_hour % 1 !== 0) {
            timeRangeStart.setMinutes(30);
        }
        if (order.del_end % 1 !== 0) {
            timeRangeEnd.setMinutes(30);
        }

        timeRangeStart = timeRangeStart.toISOString();
        timeRangeEnd = timeRangeEnd.toISOString();
    }

    const {
        ['2_man_team']: twoManTeam,
        ['3_man_team']: threeManTeam,
        lift_gate,
        ...otherEquipment
    } = useMemo(() => {
        return order.recommended_equipment
            ? Object.keys(order.recommended_equipment)
                  .filter((key) => order.recommended_equipment[key])
                  .reduce((acc, val) => ({ ...acc, [val]: recommendedEquipmentLabels[val] }), {})
            : {};
    }, [order]);

    const CardRow = ({ children, ...rest }) => {
        return (
            <Grid
                container
                item
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                style={{ marginTop: '0.25rem', marginBottom: '0.25rem' }}
                {...rest}
            >
                {children}
            </Grid>
        );
    };

    // Group items by item_type_details so the header can show distinct-type counts
    // (e.g. "cabinets: 12 | trim: 3"). No mapping/relabeling — the raw value renders.
    const itemTypeCounts = useMemo(() => {
        const counts = {};
        (order?.itemsByOrderId || []).forEach((i) => {
            const type = i.item_type_details || 'unspecified';
            counts[type] = (counts[type] || 0) + (i.quantity || 1);
        });
        return counts;
    }, [order]);

    const itemTypeSummary = Object.entries(itemTypeCounts)
        .map(([type, count]) => `${type}: ${count}`)
        .join(' | ');

    return (
        <>
            <OnwardCard>
                <CardRow>
                    <Grid
                        item
                        css={css`
                            margin-bottom: 0.5rem;
                        `}
                    >
                        <CardTitle>Items ({order?.itemsByOrderId?.length || '-'})</CardTitle>
                    </Grid>
                </CardRow>
                {itemTypeSummary && (
                    <CardRow>
                        <Grid
                            item
                            css={css`
                                margin-bottom: 1.25rem;
                            `}
                        >
                            <CardSubtitle>{itemTypeSummary}</CardSubtitle>
                        </Grid>
                    </CardRow>
                )}

                {(order?.itemsByOrderId || []).map((i, idx) => (
                    <React.Fragment key={`item-${i.item_id}`}>
                        <CardRow>
                            <Grid item>
                                <CardItemBold>Line Item #{idx + 1}</CardItemBold>
                            </Grid>
                        </CardRow>
                        <CardRow>
                            <Grid item>
                                <CardItem>{i.description || 'Description Not Provided'}</CardItem>
                            </Grid>
                        </CardRow>
                        {i.barcode && (
                            <CardRow>
                                <Grid item>
                                    <CardItem>Barcode: {i.barcode}</CardItem>
                                </Grid>
                            </CardRow>
                        )}
                        {i.sku && (
                            <CardRow>
                                <Grid item>
                                    <CardItem>SKU: {i.sku}</CardItem>
                                </Grid>
                            </CardRow>
                        )}
                        <CardRow>
                            <Grid item>
                                <CardItem>Quantity: {i.quantity}</CardItem>
                            </Grid>
                        </CardRow>
                        <CardRow>
                            <Grid item>
                                <CardItem>Total Cubes: {i?.total_cubes?.toFixed(1) || ''}</CardItem>
                            </Grid>
                        </CardRow>
                        <CardRow>
                            <Grid item>
                                <CardItem>
                                    Dimensions:{' '}
                                    {i.length ? `${i.length}L ${i.width}W ${i.height}H` : `Dimensions Not Provided`}
                                </CardItem>
                            </Grid>
                        </CardRow>
                        <CardRow>
                            <Grid item style={{ marginBottom: '1rem' }}>
                                <CardItem>Weight per item: {i.weight ? `${i.weight}lbs` : ''}</CardItem>
                            </Grid>
                        </CardRow>
                    </React.Fragment>
                ))}

                <CardRow>
                    <Grid
                        item
                        css={css`
                            margin-top: 1.75rem;
                        `}
                    >
                        <CardTitle>Haulaway</CardTitle>
                    </Grid>
                </CardRow>
                {!!order?.haulaway_items?.length ? (
                    order.haulaway_items.map((i, idx) => (
                        <React.Fragment key={`haulaway-${idx}`}>
                            <CardRow>
                                <Grid item>
                                    <CardItem>{i.description || 'Description Not Provided'}</CardItem>
                                </Grid>
                            </CardRow>
                        </React.Fragment>
                    ))
                ) : (
                    <CardRow>
                        <Grid item>
                            <CardItem>None</CardItem>
                        </Grid>
                    </CardRow>
                )}

                <CardRow>
                    <Grid
                        item
                        css={css`
                            margin-top: 1.75rem;
                        `}
                    >
                        <CardTitle>Warehouse</CardTitle>
                    </Grid>
                </CardRow>
                <PhotoRow urls={order?.warehouse_file || []} keyPrefix="warehouse" />

                <CardRow>
                    <Grid
                        item
                        css={css`
                            margin-top: 1.75rem;
                        `}
                    >
                        <CardTitle>BOL</CardTitle>
                    </Grid>
                </CardRow>
                <PhotoRow urls={order?.bol_file || []} keyPrefix="bol" />

                <CardRow>
                    <Grid
                        item
                        css={css`
                            margin-top: 1.75rem;
                        `}
                    >
                        <CardTitle>Waybill</CardTitle>
                    </Grid>
                </CardRow>
                <PhotoRow urls={order?.waybill || []} keyPrefix="waybill" />

                <CardRow>
                    <Grid
                        item
                        css={css`
                            margin-top: 1.75rem;
                        `}
                    >
                        <CardTitle>Pickup Notification</CardTitle>
                    </Grid>
                </CardRow>
                <PhotoRow urls={order?.pickup_notification || []} keyPrefix="pickup_notification" />

                <CardRow>
                    <Grid
                        item
                        css={css`
                            margin-top: 1.75rem;
                        `}
                    >
                        <CardTitle>POD</CardTitle>
                    </Grid>
                </CardRow>
                <PhotoRow urls={(order?.pod || []).map((p) => p.url)} keyPrefix="pod" />

                <CardRow>
                    <Grid
                        item
                        css={css`
                            margin-top: 1.75rem;
                        `}
                    >
                        <CardTitle>Final Delivery Photos</CardTitle>
                    </Grid>
                </CardRow>
                <PhotoRow urls={(order?.delivery_photo || []).map((p) => p.url)} keyPrefix="delivery_photo" />

                <CardRow>
                    <Grid
                        item
                        css={css`
                            margin-top: 1.75rem;
                        `}
                    >
                        <CardTitle>Customer Signature</CardTitle>
                    </Grid>
                </CardRow>
                {order?.customer_signature || order?.customer_signature_meta?.name ? (
                    <>
                        {order?.customer_signature && (
                            <CardRow>
                                <Grid item container>
                                    <a href={order.customer_signature} target="_blank" rel="noreferrer">
                                        <img
                                            src={order.customer_signature}
                                            css={css`
                                                max-width: 100%;
                                                max-height: 200px;
                                                height: auto;
                                                width: auto;
                                                object-fit: contain;
                                            `}
                                        />
                                    </a>
                                </Grid>
                            </CardRow>
                        )}
                        {order?.customer_signature_meta?.name && (
                            <>
                                <CardRow>
                                    <Grid item>Signature manually entered for:</Grid>
                                </CardRow>
                                <CardRow>
                                    <CardItemBold>{order?.customer_signature_meta?.name}</CardItemBold>
                                </CardRow>
                            </>
                        )}
                    </>
                ) : (
                    <CardRow>
                        <Grid item>
                            <CardItem>No signature</CardItem>
                        </Grid>
                    </CardRow>
                )}
            </OnwardCard>
        </>
    );
}
